<template>
  <Breadcrumb :class="prefixCls">
    <template v-for="(routeItem, index) in routes" :key="routeItem.name">
      <BreadcrumbItem>
        <MIcon v-if="getIcon(routeItem)" :icon="getIcon(routeItem)" />
        <span
          v-if="!hasRedirect(routes, routeItem) || (routes.length > 1 && index === 0)"
          class="cursor-not-allowed"
        >
          {{ t((routeItem.meta?.title || routeItem.name) as string) }}
        </span>
        <router-link v-else to="" @click="handleClick(routeItem)">
          {{ t((routeItem.meta?.title || routeItem.name) as string) }}
        </router-link>
      </BreadcrumbItem>
    </template>
    <BreadcrumbItem v-if="!routes.length">
      {{ t((route.meta?.title || route.name) as string) }}
    </BreadcrumbItem>
  </Breadcrumb>
</template>
<script lang="ts" setup>
  import { Breadcrumb, BreadcrumbItem } from 'ant-design-vue';
  import { ref, watch } from 'vue';
  import { useRoute } from 'vue-router';
  import { getPathFromHistory, treeHelper } from '@mts-fe/utils';
  import { useDesign } from '@mts/hooks/web/useDesign';
  import { t } from '@mts/hooks/web/useI18n';
  import { useGo } from '@mts/hooks/web/usePage';
  import { REDIRECT_NAME } from '@/router/constant';
  import { getAllParentPath } from '@/router/helper/menuHelper';
  import { getMenus } from '@/router/menus';
  import type { Menu } from '@/router/types';

  defineOptions({ name: 'MenuBreadcrumb' });

  const routes = ref<Menu[]>([]);
  const route = useRoute();
  const go = useGo();

  const { prefixCls } = useDesign('menu-breadcrumb');

  watch(
    () => route,
    (route) => {
      if (route.name === REDIRECT_NAME) return;

      const menus = getMenus();
      const parent = getAllParentPath(menus, route.path);
      const filterMenus = menus.filter((item) => item.path === parent[0]);
      const matched = getMatched(filterMenus, parent);
      routes.value = matched?.length
        ? filterItem(matched).filter((item) => item.meta?.parentPath !== '/')
        : [];
    },
    { immediate: true },
  );

  function getMatched(menus: Menu[], parent: string[]) {
    const matched: Menu[] = [];
    menus.forEach((item) => {
      if (parent.includes(item.path)) {
        matched.push({
          ...item,
          name: item.meta?.title || item.name,
        });
      }
      if (item.children?.length) {
        matched.push(...getMatched(item.children, parent));
      }
    });
    return matched;
  }

  function filterItem(list: Menu[]) {
    return treeHelper
      .filter(list, (item) => {
        const { meta, name } = item;
        if (!meta) {
          return !!name;
        }
        const { title, hideBreadcrumb } = meta;
        if (!title || hideBreadcrumb) {
          return false;
        }
        return true;
      })
      .filter((item) => !item.meta?.hideBreadcrumb);
  }

  function handleClick(item: Menu) {
    go(getPathFromHistory(item.path), true);
  }

  function hasRedirect(routes, route) {
    return routes.indexOf(route) !== routes.length - 1;
  }

  function getIcon(route) {
    return route.icon || route.meta?.icon;
  }
</script>

<style lang="less">
  @prefix-cls: ~'@{namespace}-menu-breadcrumb';

  .ant-breadcrumb.@{prefix-cls} {
    color: #6f7680;

    .ant-breadcrumb-separator,
    a {
      color: #6f7680;
    }

    li:last-child > .ant-breadcrumb-link {
      font-weight: 500;
    }
  }
</style>
